import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';

import { dayjs } from '../../../../core/dayjs/dayjs';
import { FreyaCommonModule } from '../../../../freya-common/freya-common.module';
import { SharedModule } from '../../../../shared/shared.module';
import { DispatchTooltipComponent } from '../dispatch-tooltip/dispatch-tooltip.component';

// Zone tz — dayjs.tz default set by TimezoneHelperService
const formatTime = (timestamp: number) =>
    dayjs.tz(timestamp * 1000).format('hh:mm A');

@Component({
  standalone: true,
  imports: [
    FreyaCommonModule,
    SharedModule,
    DispatchTooltipComponent
  ],
  selector: 'app-dispatch-chip',
  templateUrl: './dispatch-chip.component.html',
  styleUrl: './dispatch-chip.component.scss'
})
export class DispatchChipComponent {
  @Input() panelType: 'left' | 'right' | 'dialog' = 'right';
  @Input() imageUrl: string = '';
  @Input() name: string = '';
  @Input() icon: string = null;
  @Input() eventsCount: number = null;
  @Input() revenue: number = null;
  @Input() showStar: boolean = false;
  @Input() isDriver: boolean = false;
  @Input() removable: boolean = false;
  @Input() hasConflict: boolean = false;
  /** Ids of the events this person is double-booked on (hover tooltip) */
  @Input() conflictingEventIds: string[] = [];
  /** The board's events, used to resolve conflicting event titles + assets */
  @Input() events: any[] = [];
  @Input() isOvertime: boolean = false;
  @Input() rateHourly: number = null;
  /** Labor cost for this person on the viewed day */
  @Input() totalCost: number = null;
  /** Overtime hours worked on the viewed day */
  @Input() overtimeHours: number = null;
  /** Hours worked this week (for OT context) */
  @Input() hoursWorkedThisWeek: number = null;
  /** The person's weekly availability array (day-name strings) */
  @Input() availability: any = null;
  /** Extra text shown in the info-circle tooltip (e.g. rating + effective rate) */
  @Input() tooltipText: string = '';
  @Input() showLocked = false;
  @Input() isUnavailable = false;
  @Input() isBookedOff = false;
  @Input() isAssigned = false;
  @Output() remove = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();

  /** Conflict hover overlay — lives inside *ngIf="hasConflict", so it only
   *  exists when it can be shown. ViewChild (non-static) resolves on creation. */
  @ViewChild('conflictOp') conflictOp: OverlayPanel;

  /**
   * Resolves the conflicting event ids to display rows for the hover tooltip.
   * Shows the JOB CODE + customer name (Ray: the event id means nothing to a
   * dispatcher), the time window, and the truck(s) on each event.
   *
   * The parent passes the route groups (selectRouteGroupsWithMetrics); the
   * board events live under each group's `.events` as wrapped DispatchEvents
   * (.event = the CalendarEvent fragment). Flat unwrapped events are supported
   * as a fallback.
   */
  public get conflictDetails(): { id: string; title: string; time: string; assets: string }[] {
    const boardEvents = (this.events || []).flatMap((g) => {
      if (g && Array.isArray(g.events)) { return g.events; }
      return [g];
    });
    return (this.conflictingEventIds || []).map((id) => {
      const ev = boardEvents.find((e) => (e?.event?.id || e?.id) === id);
      const job = ev?.event?.job ?? ev?.job;
      const customerUser = (job?.users || []).find((u: any) => u?.role === 'customer')?.user;
      const customerName = customerUser
        ? (customerUser.company || [customerUser.givenName, customerUser.familyName].filter(Boolean).join(' '))
        : null;
      const title = [job?.code, customerName].filter(Boolean).join(' — ')
        || ev?.event?.title
        || ev?.title
        || 'Unknown event';
      const start = ev?.event?.start ?? ev?.start;
      const end = ev?.event?.end ?? ev?.end;
      const time = start && end
        ? `${formatTime(start)} – ${formatTime(end)}`
        : '';
      const assets = (ev?.event?.assets || ev?.assets || [])
        .map((a: any) => a?.name)
        .filter(Boolean)
        .join(', ') || 'No assets';
      return { id, title, time, assets };
    });
  }

  public onConflictHover(event: MouseEvent) {
    if (this.hasConflict && this.conflictOp) { this.conflictOp.show(event); }
  }

  public onConflictLeave() {
    if (this.hasConflict && this.conflictOp) { this.conflictOp.hide(); }
  }

  public onRemove() {
    this.remove.emit();
  }

  public onEdit(){
    this.edit.emit();
  }
}